import { useCallback, useMemo, useState } from 'react';

import { AdminPermissions } from 'constants/admin-permissions';
import { useAccessControl } from 'hooks/access-control';
import { useDocumentTitle } from 'hooks/document-title';
import AdminPage from 'layouts/AdminPage/AdminPage';
import { useSelector } from 'react-redux';

import { Button } from 'components/FormComponents';
import { Table, useTableContext } from 'components/Table/Table';

import { selectAuthentication } from 'api/authentication-slice';
import {
  useGetCheckTypePricingQuery,
  useLazyGetCompanyChecksReportQuery,
  useUpdateCheckTypePricingMutation,
} from 'api/check-type-pricing-api';
import { useGetCheckGroupsQuery, useGetCheckTypesQuery } from 'api/check-types-api';

const COUNTRY_OPTIONS = [
  { name: 'ALL', value: 'all' },
  { name: 'INT', value: 'INT' },
  { name: 'NZ', value: 'NZ' },
  { name: 'AU', value: 'AU' },
  { name: 'US', value: 'US' },
  { name: 'UK', value: 'UK' },
];

const HAS_SUBCHECKS_OPTIONS = [
  { name: 'ALL', value: 'all' },
  { name: 'Has Subchecks', value: 'yes' },
  { name: 'No Subchecks', value: 'no' },
];

const COST_CURRENCY_OPTIONS = [
  { name: '', value: '' },
  { name: 'AUD', value: 'AUD' },
  { name: 'NZD', value: 'NZD' },
  { name: 'USD', value: 'USD' },
];

const costCurrencyDisplay = v => (v ? String(v).toUpperCase() : '-');

const ApplyFiltersButton = () => {
  const { manuallyApplyFilters } = useTableContext();
  return (
    <Button icon="filter" onClick={manuallyApplyFilters}>
      Apply Filters
    </Button>
  );
};

const PricingTableBody = ({
  tableRows,
  canEdit,
  showCost,
  pendingEdits,
  onPendingChange,
  saving,
}) => {
  const { queryParams } = useTableContext();
  const searchTerm = (queryParams.search?.value ?? '').toLowerCase().trim();
  const filtered = !searchTerm
    ? tableRows
    : tableRows.filter(
        row =>
          (row.title ?? '').toLowerCase().includes(searchTerm) ||
          (row.type ?? '').toLowerCase().includes(searchTerm) ||
          (row.check_group_title ?? '').toLowerCase().includes(searchTerm) ||
          (row.charge_type_display ?? '').toLowerCase().includes(searchTerm) ||
          (row.source_display ?? '').toLowerCase().includes(searchTerm) ||
          (row.source_value ?? '').toLowerCase().includes(searchTerm) ||
          (row.provider ?? '').toLowerCase().includes(searchTerm),
      );

  const getValue = (row, field) => {
    const edited = pendingEdits[row.id];
    if (edited && field in edited) return edited[field] === null ? '' : String(edited[field]);
    const v = row[field];
    return v === null || v === undefined ? '' : String(v);
  };

  const formatPrice = v =>
    v != null && v !== '' && !Number.isNaN(Number(v)) ? Number(v).toFixed(2) : '-';

  const formatPriceInputValue = (row, field) => {
    const v = getValue(row, field);
    if (v === '') return '';
    const n = Number(v);
    return Number.isNaN(n) ? v : n.toFixed(2);
  };

  const handlePendingChange = useCallback(
    (row, field, e, { type = 'string', trim = false } = {}) => {
      const raw = e.target.value;
      if (type === 'number') {
        const num = raw === '' ? null : parseFloat(raw);
        onPendingChange(row.id, field, num === null || Number.isNaN(num) ? null : num);
      } else {
        const v = trim ? raw.trim() : raw;
        onPendingChange(row.id, field, v === '' ? null : v);
      }
    },
    [onPendingChange],
  );

  return (
    <>
      {filtered.map(row => (
        <Table.Row key={`pricing-${row.id ?? ''}-${row.charge_type ?? ''}`}>
          <Table.Col>{row.title ?? '-'}</Table.Col>
          <Table.Col>{row.charge_type_display ?? row.charge_type ?? '-'}</Table.Col>
          <Table.Col>{row.source_display ?? '-'}</Table.Col>
          <Table.Col>
            {canEdit ? (
              <input
                type="text"
                className="form-control form-control-sm"
                value={getValue(row, 'provider')}
                onChange={e => handlePendingChange(row, 'provider', e, { trim: true })}
                disabled={saving}
                placeholder="Provider"
                aria-label={`Provider for ${row.title}`}
              />
            ) : (
              (row.provider ?? '-')
            )}
          </Table.Col>
          <Table.Col>
            {canEdit ? (
              <input
                type="number"
                step="0.01"
                min="0"
                className="form-control form-control-sm"
                value={formatPriceInputValue(row, 'price_aud')}
                onChange={e => handlePendingChange(row, 'price_aud', e, { type: 'number' })}
                disabled={saving}
                aria-label={`AUD for ${row.title}`}
              />
            ) : (
              formatPrice(row.price_aud)
            )}
          </Table.Col>
          <Table.Col>
            {canEdit ? (
              <input
                type="number"
                step="0.01"
                min="0"
                className="form-control form-control-sm"
                value={formatPriceInputValue(row, 'price_nzd')}
                onChange={e => handlePendingChange(row, 'price_nzd', e, { type: 'number' })}
                disabled={saving}
                aria-label={`NZD for ${row.title}`}
              />
            ) : (
              formatPrice(row.price_nzd)
            )}
          </Table.Col>
          <Table.Col>
            {canEdit ? (
              <input
                type="number"
                step="0.01"
                min="0"
                className="form-control form-control-sm"
                value={formatPriceInputValue(row, 'price_usd')}
                onChange={e => handlePendingChange(row, 'price_usd', e, { type: 'number' })}
                disabled={saving}
                aria-label={`USD for ${row.title}`}
              />
            ) : (
              formatPrice(row.price_usd)
            )}
          </Table.Col>
          {showCost && (
            <>
              <Table.Col>
                {canEdit ? (
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="form-control form-control-sm"
                    value={formatPriceInputValue(row, 'cost')}
                    onChange={e => handlePendingChange(row, 'cost', e, { type: 'number' })}
                    disabled={saving}
                    aria-label={`Cost for ${row.title}`}
                  />
                ) : (
                  formatPrice(row.cost)
                )}
              </Table.Col>
              <Table.Col>
                {canEdit ? (
                  <select
                    className="form-control form-control-sm"
                    value={getValue(row, 'cost_currency') || ''}
                    onChange={e => handlePendingChange(row, 'cost_currency', e)}
                    disabled={saving}
                    aria-label={`Cost currency for ${row.title}`}>
                    {COST_CURRENCY_OPTIONS.map(opt => (
                      <option key={opt.value || 'empty'} value={opt.value}>
                        {opt.name || '-'}
                      </option>
                    ))}
                  </select>
                ) : (
                  costCurrencyDisplay(row.cost_currency)
                )}
              </Table.Col>
            </>
          )}
        </Table.Row>
      ))}
    </>
  );
};

const CheckTypePricingTab = () => {
  const authentication = useSelector(selectAuthentication);
  const hasPermission = useAccessControl(authentication?.admin_permissions);
  const canEdit = hasPermission(AdminPermissions.UpdatePricing);
  const showCost = hasPermission(AdminPermissions.ListCost);

  const { data: checkGroups = [] } = useGetCheckGroupsQuery();
  const { data: allCheckTypes = [] } = useGetCheckTypesQuery();
  const { data: checkTypePricing = [], isLoading, refetch } = useGetCheckTypePricingQuery();
  const [updateCheckTypePricing] = useUpdateCheckTypePricingMutation();
  const [getCompanyChecksReport] = useLazyGetCompanyChecksReportQuery();
  const [saving, setSaving] = useState(false);
  const [pendingEdits, setPendingEdits] = useState({});
  const [reportDownloading, setReportDownloading] = useState(false);

  const checkTypePricingList = useMemo(
    () => (Array.isArray(checkTypePricing) ? checkTypePricing : []),
    [checkTypePricing],
  );

  const onPendingChange = useCallback((rowId, field, value) => {
    setPendingEdits(prev => ({
      ...prev,
      [rowId]: { ...(prev[rowId] ?? {}), [field]: value },
    }));
  }, []);

  const handleSave = useCallback(() => {
    const ids = Object.keys(pendingEdits);
    if (ids.length === 0) return;
    setSaving(true);
    const updates = ids.map(id =>
      updateCheckTypePricing({ id: Number(id), ...pendingEdits[id] }).unwrap(),
    );
    Promise.all(updates)
      .then(() => {
        setPendingEdits({});
        return refetch();
      })
      .catch(err => {
        console.error('Save check type pricing:', err?.data?.message || err?.message, err);
      })
      .finally(() => setSaving(false));
  }, [pendingEdits, updateCheckTypePricing, refetch]);

  const checkGroupOptions = [
    { name: 'All Groups', value: 'all' },
    ...checkGroups.map(opt => ({ name: opt.name ?? opt.value, value: opt.value })),
  ];

  const checkTypeOptions = useMemo(() => {
    const types = (allCheckTypes || [])
      .filter(ct => !ct.deprecated)
      .map(ct => ({ name: ct.title, value: ct.slug }))
      .sort((a, b) => a.name.localeCompare(b.name));
    return [{ name: 'All Types', value: 'all' }, ...types];
  }, [allCheckTypes]);

  const defaultQuery = {
    page: { value: 1 },
    check_group: {
      value: ['all'],
      name: ['All Groups'],
      type: 'status',
      title: 'Check Group',
    },
    check_type: {
      value: ['all'],
      name: ['All Types'],
      type: 'status',
      title: 'Check Type',
    },
    country: {
      value: ['all'],
      name: ['ALL'],
      type: 'status',
      title: 'Country',
    },
    has_subchecks: {
      value: 'all',
      name: 'ALL',
      title: 'Subchecks',
    },
  };

  const [tableQuery, setTableQuery] = useState(defaultQuery);

  const tableRows = useMemo(() => {
    if (!checkTypePricingList.length) return [];
    const q = tableQuery || {};
    const getVal = key => {
      const v = q[key];
      const val = v != null && typeof v === 'object' && 'value' in v ? v.value : v;
      return Array.isArray(val) ? val : val != null && val !== '' ? [val] : [];
    };
    const checkGroupsFilter = getVal('check_group').filter(x => x !== 'all');
    const checkTypesFilter = getVal('check_type').filter(x => x !== 'all');
    const countries = getVal('country').filter(x => x !== 'all');
    const hasSubchecks = q.has_subchecks?.value ?? q.has_subchecks ?? 'all';

    let rows = checkTypePricingList.map(r => ({
      ...r,
      check_group_title: r.check_group_title ?? r.check_group ?? '-',
    }));

    if (checkGroupsFilter.length > 0)
      rows = rows.filter(r => checkGroupsFilter.includes(r.check_group));
    if (checkTypesFilter.length > 0) rows = rows.filter(r => checkTypesFilter.includes(r.type));
    if (countries.length > 0) {
      rows = rows.filter(r => {
        const ca = r.countries_available;
        const arr = Array.isArray(ca)
          ? ca
          : typeof ca === 'string'
            ? (() => {
                try {
                  return JSON.parse(ca || '[]') ?? [];
                } catch {
                  return [];
                }
              })()
            : [];
        return countries.some(c => arr.includes(c));
      });
    }
    if (hasSubchecks === 'yes') rows = rows.filter(r => !!r.has_subchecks);
    else if (hasSubchecks === 'no') rows = rows.filter(r => !r.has_subchecks);

    const sourceOrder = v => {
      if (v == null || v === '') return 0;
      if (v === 'AU') return 1;
      if (v === 'NZ') return 2;
      if (v === 'US') return 3;
      return 4;
    };
    rows.sort((a, b) => {
      const ta = (a.title ?? '').localeCompare(b.title ?? '');
      if (ta !== 0) return ta;
      const da = a.is_default ? 1 : 0;
      const db = b.is_default ? 1 : 0;
      if (db !== da) return db - da;
      const scopeOrder = v =>
        v === 'per_check' ? 0 : v === 'per_subcheck' ? 1 : v === 'per_item' ? 2 : 3;
      const sa = scopeOrder(a.charge_type);
      const sb = scopeOrder(b.charge_type);
      if (sa !== sb) return sa - sb;
      const oa = sourceOrder(a.source_value);
      const ob = sourceOrder(b.source_value);
      if (oa !== ob) return oa - ob;
      return (a.source_value ?? '').localeCompare(b.source_value ?? '');
    });
    return rows;
  }, [checkTypePricingList, tableQuery]);

  const handleDownloadCsv = useCallback(() => {
    const escape = v => {
      const s = v == null ? '' : String(v);
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"`
        : s;
    };
    const boolYesNo = v => (v ? 'Yes' : 'No');
    const headers = [
      'Check Type Title',
      'Check Group',
      'Type',
      'Charge Type',
      'Source',
      'Source Value',
      'Default',
      'Provider',
      'AUD',
      'NZD',
      'USD',
      ...(showCost ? ['Cost', 'Cost Currency'] : []),
      'Countries Available',
      'Has Subchecks',
      'Bill per Subcheck',
      'Updated At',
      'Updated By',
    ];
    const csvRows = [
      headers.join(','),
      ...tableRows.map(r => {
        const countriesList = Array.isArray(r.countries_available)
          ? r.countries_available.join(', ')
          : typeof r.countries_available === 'string'
            ? r.countries_available
            : '';
        const sourceDisplay = r.source_display ?? '-';
        const chargeTypeDisplay = r.charge_type_display ?? r.charge_type ?? '-';
        const rowCells = [
          escape(r.title ?? ''),
          escape(r.check_group_title ?? r.check_group ?? ''),
          escape(r.type ?? ''),
          escape(chargeTypeDisplay),
          escape(sourceDisplay),
          escape(r.source_value ?? ''),
          boolYesNo(r.is_default),
          escape(r.provider ?? ''),
          escape(r.price_aud ?? ''),
          escape(r.price_nzd ?? ''),
          escape(r.price_usd ?? ''),
          ...(showCost ? [escape(r.cost ?? ''), escape(r.cost_currency ?? '')] : []),
          escape(countriesList),
          boolYesNo(r.has_subchecks),
          escape(r.bill_per_subcheck != null ? (r.bill_per_subcheck ? 'Yes' : 'No') : '-'),
          escape(r.updated_at ?? ''),
          escape((r.updated_by_name ?? '').trim() || (r.updated_by_email ?? '')),
        ];
        return rowCells.join(',');
      }),
    ];
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'check_type_pricing_export.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, [tableRows, showCost]);

  const handleDownloadCompanyChecksPricing = useCallback(() => {
    setReportDownloading(true);
    getCompanyChecksReport()
      .unwrap()
      .finally(() => setReportDownloading(false));
  }, [getCompanyChecksReport]);

  return (
    <Table.Context
      defaultQuery={defaultQuery}
      manualQueryUpdate
      onQueryUpdate={setTableQuery}
      multiSelectNormalize={{
        check_group: { allValue: 'all', allName: 'All Groups' },
        check_type: { allValue: 'all', allName: 'All Types' },
        country: { allValue: 'all', allName: 'ALL' },
      }}>
      <Table.FloatingFilters className="u-margin--large surface-white">
        <Table.FloatingFiltersLeft className="u-gap-medium align-items-end">
          <Table.MultiSelectFilter
            title="Check Group"
            filterName="check_group"
            type="status"
            options={checkGroupOptions}
            useDefault={false}
            useSearchInMultiSelect
          />
          <Table.MultiSelectFilter
            title="Check Type"
            filterName="check_type"
            type="status"
            options={checkTypeOptions}
            useSearchInMultiSelect
          />
          <Table.MultiSelectFilter
            title="Country"
            filterName="country"
            type="status"
            options={COUNTRY_OPTIONS}
            useDefault={false}
            useSearchInMultiSelect
          />
          <Table.Filter
            title="Subchecks"
            filterName="has_subchecks"
            options={HAS_SUBCHECKS_OPTIONS}
            useDefault={false}
          />
        </Table.FloatingFiltersLeft>
        <Table.FloatingFiltersRight>
          <ApplyFiltersButton />
        </Table.FloatingFiltersRight>
      </Table.FloatingFilters>
      <Table.TableContent className="u-margin--large">
        <div
          className="u-padding-x--large u-padding-top"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            flexWrap: 'wrap',
            gap: '0.5rem',
          }}>
          <Table.Search placeholder="Search pricing..." />
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {canEdit && (
              <Button
                icon="download"
                onClick={handleDownloadCompanyChecksPricing}
                disabled={reportDownloading}>
                {reportDownloading ? 'Downloading…' : 'Company Checks Pricing Report'}
              </Button>
            )}
            <Button icon="download" onClick={handleDownloadCsv}>
              Download CSV
            </Button>
            {canEdit && (
              <Button
                type="primary"
                icon="save"
                onClick={handleSave}
                disabled={saving || Object.keys(pendingEdits).length === 0}>
                Save
              </Button>
            )}
          </div>
        </div>
        <div className="u-margin--large">
          <Table>
            <Table.Head>
              <Table.Row key="header-row">
                <Table.Header key="title">Title</Table.Header>
                <Table.Header key="charge_type">Charge Type</Table.Header>
                <Table.Header key="source_value">Source Value</Table.Header>
                <Table.Header key="provider">Provider</Table.Header>
                <Table.Header key="price_aud">AUD</Table.Header>
                <Table.Header key="price_nzd">NZD</Table.Header>
                <Table.Header key="price_usd">USD</Table.Header>
                {showCost && (
                  <>
                    <Table.Header key="cost">Cost</Table.Header>
                    <Table.Header key="cost_currency">Cost Currency</Table.Header>
                  </>
                )}
              </Table.Row>
            </Table.Head>
            <Table.Body>
              <PricingTableBody
                tableRows={tableRows}
                canEdit={canEdit}
                showCost={showCost}
                pendingEdits={pendingEdits}
                onPendingChange={onPendingChange}
                saving={saving}
              />
            </Table.Body>
          </Table>
        </div>
        {tableRows.length === 0 && !isLoading && (
          <p className="secondary-text u-padding--large">No pricing records match the filters.</p>
        )}
      </Table.TableContent>
    </Table.Context>
  );
};

const AdminPricing = () => {
  useDocumentTitle('Pricing');

  return (
    <AdminPage title="Pricing" allowedRole={AdminPermissions.ListPricing}>
      <CheckTypePricingTab />
    </AdminPage>
  );
};

export default AdminPricing;
